/**
 * Mentions: the composer's `@` picker and the transcript's pill, as pure
 * functions — the test environment has no DOM.
 *
 * A mention is an account id in a message's `mentions` (the wire's `mn`),
 * never a name in its text. The text carries the visible `@Name` so a client
 * that has never heard of `mn` still shows the words; a pill is drawn only
 * where an id the message carries meets its own `@Name` in the body.
 */

import type { Person } from '../../lib/relay/types';

/** Somebody the picker can offer. A plain record in a plain array, so an entry
 *  that is not a member is one more item rather than a new shape. */
export interface MentionCandidate {
  id: string;
  name: string;
}

/** The conversation's members, minus you, as the picker's list. */
export function mentionCandidates(people: Person[], meId: string | undefined): MentionCandidate[] {
  return people.filter((p) => p.id !== meId && p.name).map((p) => ({ id: p.id, name: p.name }));
}

/** Past this, what follows the `@` is a sentence rather than a name. */
const QUERY_MAX = 40;

/** How many the picker shows at once. */
const SHOWN_MAX = 8;

/**
 * The `@` being typed at `caret`, or null.
 *
 * An `@` counts at the start or after whitespace, so an address opens nothing.
 * What follows it may hold spaces, because names do, but not a line break.
 */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf('@');
  if (start < 0) return null;
  if (start > 0 && !/\s/.test(before[start - 1]!)) return null;
  const query = before.slice(start + 1);
  if (query.length > QUERY_MAX || query.includes('\n')) return null;
  return { start, query };
}

/** The candidates whose name, or a word in it, starts with the query. */
export function matchMentions(candidates: MentionCandidate[], query: string): MentionCandidate[] {
  const q = query.toLocaleLowerCase();
  return candidates
    .filter((c) => {
      const name = c.name.toLocaleLowerCase();
      return name.startsWith(q) || name.split(/\s+/).some((word) => word.startsWith(q));
    })
    .slice(0, SHOWN_MAX);
}

/** The text with the query replaced by `@Name `, and where the caret goes. */
export function insertMention(
  text: string,
  start: number,
  caret: number,
  name: string
): { text: string; caret: number } {
  const inserted = `@${name} `;
  return { text: text.slice(0, start) + inserted + text.slice(caret), caret: start + inserted.length };
}

const WORD = /[\p{L}\p{N}_]/u;

/** Each `@Name` in `text` for the names given, longest name first, so
 *  "@Ada L" is not read as "@Ada" followed by " L". */
function scan(text: string, named: MentionCandidate[]): Array<{ at: number; end: number; id: string }> {
  const longest = named.filter((c) => c.name).sort((a, b) => b.name.length - a.name.length);
  const hits: Array<{ at: number; end: number; id: string }> = [];
  for (let at = text.indexOf('@'); at >= 0; at = text.indexOf('@', at + 1)) {
    const hit = longest.find(
      (c) => text.startsWith(c.name, at + 1) && !WORD.test(text[at + 1 + c.name.length] ?? '')
    );
    if (!hit) continue;
    const end = at + 1 + hit.name.length;
    hits.push({ at, end, id: hit.id });
    at = end - 1;
  }
  return hits;
}

/** What a message sends as `mentions`: the people picked whose `@Name` is
 *  still in the text, each once. */
export function mentionsToSend(text: string, picked: MentionCandidate[]): string[] {
  return [...new Set(scan(text, picked).map((hit) => hit.id))];
}

/** A body as text and pills. A string is text; an object is a pill. */
export type MentionSegment = string | { id: string; text: string };

export function mentionSegments(
  body: string,
  mentions: string[] | undefined,
  people: Person[]
): MentionSegment[] {
  if (!mentions?.length) return [body];
  const named = people.filter((p) => mentions.includes(p.id));
  const out: MentionSegment[] = [];
  let from = 0;
  for (const hit of scan(body, named)) {
    if (hit.at > from) out.push(body.slice(from, hit.at));
    out.push({ id: hit.id, text: body.slice(hit.at, hit.end) });
    from = hit.end;
  }
  if (from < body.length || out.length === 0) out.push(body.slice(from));
  return out;
}
